/**
 * «Мои задачи» партнёра на открытии (владелец 06.10.2026): карточка открытия,
 * задачи этапа и заявка на переход, помещения с перепиской и «моментами»,
 * траектория обучения по этапам, уведомления.
 *
 * Контракт — зеркало backend/src/modules/opening-tasks/opening-tasks.types.ts
 * (меняется там — правим здесь). lib/wikiApi.ts заморожен, поэтому свой модуль.
 *
 * Решения владельца: видео в переписке — до 5 минут; «момент» партнёр
 * отмечает «Сделано», куратор подтверждает; на следующий этап партнёр подаёт
 * заявку, куратор проверяет и переводит.
 */
import { api, ApiError } from '../api';

export type ThreadFileKind = 'photo' | 'video' | 'plan' | 'file';
export type MomentStatus = 'open' | 'done' | 'confirmed';
export type StageRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

/** Ограничения вложений переписки (сервер присылает свои в `RoomThreadDto.limits`). */
export interface ThreadLimits {
  maxFiles: number;
  maxPhotoMb: number;
  maxPlanMb: number;
  maxVideoMb: number;
  /** Владелец 06.10.2026: видео до 5 минут. */
  maxVideoSec: number;
  maxFileMb: number;
}

/** Запасные лимиты — пока переписка не загрузилась (те же, что на бэке). */
export const THREAD_LIMITS: ThreadLimits = {
  maxFiles: 10,
  maxPhotoMb: 20,
  maxPlanMb: 50,
  maxVideoMb: 500,
  maxVideoSec: 300,
  maxFileMb: 50,
};

export interface ThreadFileDto {
  id: string;
  kind: ThreadFileKind;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  durationSec: number | null;
  /** GET → 302 на временную ссылку хранилища (cookie-сессия); берём как есть. */
  url: string;
}

export interface ThreadMessageDto {
  id: string;
  kind: 'comment' | 'system';
  body: string;
  createdAt: string;
  /** `onBehalf` — менеджер добавил от имени партнёра: `side` = 'partner', `name` — имя менеджера. */
  author: { id: string | null; name: string; side: 'partner' | 'curator' | 'designer' | 'system'; onBehalf: boolean };
  /** Сообщение текущего пользователя. */
  mine: boolean;
  files: ThreadFileDto[];
  /** Сообщение отмечено моментом. */
  momentId: string | null;
}

export interface MomentDto {
  id: string;
  text: string;
  status: MomentStatus;
  commentId: string | null;
  createdAt: string;
  createdBy: string | null;
  doneAt: string | null;
  doneBy: string | null;
  doneNote: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
}

export interface RoomThreadDto {
  submissionId: string;
  address: string | null;
  stageCode: string | null;
  stageLabel: string | null;
  messages: ThreadMessageDto[];
  moments: MomentDto[];
  /** Новых сообщений другой стороны до этого открытия. */
  unread: number;
  limits: ThreadLimits;
}

export interface StageTaskDto {
  /** id задачи этапа; для статей раздела вики без настроенных задач — `article:<slug>`. */
  id: string;
  kind: 'article' | 'course' | 'auto' | 'manual';
  title: string;
  required: boolean;
  done: boolean;
  doneAt: string | null;
  /** Отмечается сам (прочитана статья, пройден курс, заявка дошла до стадии). */
  auto: boolean;
  /** Партнёр может отметить сам (ручной пункт). */
  canToggle: boolean;
  /** Ссылка в вики: `/a/<slug>`, `/learn/<courseId>`, `/opening/room-check`. */
  link: string | null;
}

/**
 * Пункт чек-листа задачи карточки: `photo` — просто отметка «фото сделано», загрузки нет; `link` — отметка вместе со
 * ссылкой (`url`: у отмеченного — присланный адрес, у неотмеченного — null). У `check` / `photo` поля `url` нет.
 */
export interface MyCardChecklistItemDto {
  title: string;
  kind: 'check' | 'photo' | 'link';
  done: boolean;
  url?: string | null;
}

/**
 * Результат работы УК для партнёра (блок «От управляющей компании»): задача стороны «УК» любого этапа, в чек-листе
 * которой отмечен пункт-«ссылка» (группа ВК и т. п.). Пункты — весь чек-лист задачи («Доступ выдан» тоже).
 */
export interface UkResultDto {
  id: string;
  taskTitle: string;
  status: MyCardTaskStatus;
  items: MyCardChecklistItemDto[];
}

export type MyCardTaskStatus = 'todo' | 'in_progress' | 'rework' | 'done';

/**
 * Задача карточки открытия (v2, по шаблону плана): текущий этап, сторона «партнёр» / «обе»,
 * без пропущенных. Хвосты прошлых этапов — с `carriedFromStage`.
 */
export interface MyCardTaskDto {
  id: string;
  title: string;
  description: string | null;
  status: MyCardTaskStatus;
  done: boolean;
  doneAt: string | null;
  /** Срок, `YYYY-MM-DD`. */
  dueDate: string | null;
  /** Срок раньше сегодняшнего дня по Москве и задача не выполнена. */
  overdue: boolean;
  isMilestone: boolean;
  /** Хвост: с какого этапа переехала. Название — null, если этапа нет в справочнике. */
  carriedFromStage: string | null;
  carriedFromLabel: string | null;
  side: 'partner' | 'both';
  /** Исполнитель — вы. */
  mine: boolean;
  checklist: MyCardChecklistItemDto[];
  /** Выполнение принимает проверяющий (роль / отдел — текстом). */
  requiresApproval: boolean;
  reviewerRole: string | null;
  /** Статья базы знаний; в вики — `/article/<slug>`. */
  kbArticle: { id: string; slug: string; title: string } | null;
  links: Array<{ title: string; url: string }>;
  /** Партнёр может отметить сам. */
  canToggle: boolean;
  /** Даты, которые партнёр ввёл сам («начали» / «закончим»), `YYYY-MM-DD`. Только для информации — план не меняют (FB-32). */
  partnerStartDate: string | null;
  partnerDueDate: string | null;
  /** Когда задачу взяли в работу (ISO). */
  startedAt: string | null;
}

/** Тело записи дат партнёра: ключ не передан — поле не трогаем. */
export interface PartnerDatesPatch {
  partnerStartDate?: string | null;
  partnerDueDate?: string | null;
}

/** Статусы, которые партнёр ставит из вики («На доработку» возвращает УК). */
export type MyCardTaskSetStatus = 'done' | 'todo' | 'in_progress';

export interface StageRequestDto {
  id: string;
  pipelineId: string;
  fromStageCode: string | null;
  fromStageLabel: string | null;
  toStageCode: string;
  toStageLabel: string;
  status: StageRequestStatus;
  comment: string | null;
  createdAt: string;
  createdBy: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionComment: string | null;
}

export interface StageStripItem {
  code: string;
  label: string;
  color: string | null;
  state: 'done' | 'current' | 'next' | 'locked';
}

export interface MyRoomDto {
  id: string;
  address: string | null;
  stageCode: string | null;
  stageLabel: string | null;
  status: string;
  verdict: string | null;
  /** Итоговый балл осмотра, % (если посчитан). */
  score: number | null;
  createdAt: string;
  openMoments: number;
  doneMoments: number;
  confirmedMoments: number;
  unread: number;
  lastMessageAt: string | null;
  /** YYYY-MM-DD — срок проекта, пока заявка у проектировщика. */
  designDueAt?: string | null;
}

export interface TrajectoryItemDto {
  kind: 'course' | 'article';
  id: string;
  title: string;
  link: string;
  done: boolean;
  progressPercent: number | null;
}

export interface TrajectoryGroupDto {
  /** Этап обучения (крупный: Заявка, Помещение, Ремонт, Оборудование, Персонал, Меню, Запуск). */
  stage: string;
  label: string;
  state: 'done' | 'current' | 'locked';
  items: TrajectoryItemDto[];
}

export interface MyTasksNotificationDto {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface OpeningPipelineDto {
  id: string;
  city: string;
  address: string | null;
  status: string;
  stage: { code: string; label: string; color: string | null; index: number; total: number };
  nextStage: { code: string; label: string } | null;
  stageEnteredAt: string | null;
  daysOnStage: number | null;
  plannedOpenAt: string | null;
  curator: { name: string } | null;
}

export interface MyOpeningTasksDto {
  partner: { id: string; name: string } | null;
  pipeline: OpeningPipelineDto | null;
  stages: StageStripItem[];
  /** Прежние задачи этапа (статьи, курсы, авто-пункты): идут после задач карточки. */
  stageTasks: StageTaskDto[];
  /** Задачи карточки по шаблону плана; пусто — у карточки их нет, экран как раньше. */
  cardTasks: MyCardTaskDto[];
  /** Результаты работы УК с присланными ссылками (блок «От управляющей компании»); пусто — блока нет. */
  ukResults: UkResultDto[];
  stageRequest: StageRequestDto | null;
  /** Можно подать заявку на переход (есть следующий этап и нет заявки на рассмотрении). */
  canRequestNext: boolean;
  /** Чего не хватает: невыполненные задачи карточки и обязательные прежние — подсказка партнёру, решает куратор. */
  missingRequired: string[];
  rooms: MyRoomDto[];
  trajectory: TrajectoryGroupDto[];
  notifications: MyTasksNotificationDto[];
}

// ───────────────────────── Визард «Открытие точки» (владелец 31.10.2026) ─────────────────────────
// Зеркало backend/src/modules/opening-tasks/opening-tasks.types.ts (MyOpeningPlanDto и соседи).

/**
 * Задача плана открытия: ВСЕ задачи карточки (все этапы, обе стороны, без «Пропущено»). Поля — как у
 * `MyCardTaskDto` (задача «только УК» — `canToggle: false`), плюс этап, отдел и даты бара Ганта.
 */
export interface MyPlanTaskDto extends Omit<MyCardTaskDto, 'side'> {
  stageCode: string;
  side: 'partner' | 'uk' | 'both';
  deptLabel: string | null;
  /** Начало бара, `YYYY-MM-DD`. */
  startDate: string | null;
  ganttStatus: 'done' | 'rework' | 'in_progress' | 'blocked' | 'forecast' | 'plan' | 'skipped';
  attention: 'overdue' | 'soon' | null;
  /** id задач этой же карточки, после которых идёт задача (для «можно делать на этой неделе»). */
  dependsOn: string[];
}

export interface MyPlanStageDto {
  code: string;
  label: string;
  phaseCode: string | null;
  deptLabel: string | null;
  /** Окно этапа, `YYYY-MM-DD`. */
  start: string;
  end: string;
  estimated: boolean;
  state: 'done' | 'current' | 'future';
  tasksDone: number;
  tasksTotal: number;
}

export interface MyPlanEventDto {
  id: string;
  kind: string;
  text: string;
  actorName: string | null;
  createdAt: string;
}

export interface MyPlanSubmissionDto {
  id: string;
  type: string;
  typeLabel: string;
  status: string;
  stageLabel: string | null;
  createdAt: string;
}

export interface MyPlanCardDto {
  id: string;
  partnerName: string;
  city: string;
  address: string | null;
  statusV2: 'active' | 'paused' | 'opened' | 'archived' | 'cancelled';
  stage: { code: string; label: string; index: number; total: number };
  nextStage: { code: string; label: string } | null;
  daysOnStage: number;
  normDays: number | null;
  overNorm: boolean;
  curatorName: string | null;
  startedAt: string;
  openedAt: string | null;
  leaseSignedAt: string | null;
  baseOpenAt: string | null;
  feePaidAt: string | null;
  pauseReason: string | null;
  today: string;
  lease: { date: string; estimated: boolean };
  techOpen: { date: string; estimated: boolean };
  officialOpen: { date: string; estimated: boolean };
  supportUntil: string;
  paused: boolean;
  phases: Array<{ code: string; label: string }>;
  stages: MyPlanStageDto[];
  tasks: MyPlanTaskDto[];
  overdueTasks: number;
  soonTasks: number;
  events: MyPlanEventDto[];
  submissions: MyPlanSubmissionDto[];
  ukResults: UkResultDto[];
}

/** `GET /wiki/opening/plan`: карточки нет — `card: null` (визард показывает прежний экран). */
export interface MyOpeningPlanDto {
  card: MyPlanCardDto | null;
}

/**
 * Строка списка открытий для менеджера УК (`GET /wiki/opening/plans`, владелец 31.10.2026): карточки в скоупе CRM,
 * право «Этапы открытия — просмотр». Зеркало `OpeningPlanListItemDto` бэкенда.
 */
export interface OpeningPlanListItemDto {
  id: string;
  /** ИП или кандидат (договор франшизы ещё не подписан). */
  title: string;
  candidate: boolean;
  city: string;
  address: string | null;
  statusV2: 'active' | 'paused' | 'opened' | 'archived' | 'cancelled';
  stage: { code: string; label: string };
  daysOnStage: number;
  normDays: number | null;
  overNorm: boolean;
  /** Задачи текущего этапа: выполнено / всего. */
  tasksDone: number;
  tasksTotal: number;
  overdueTasks: number;
  soonTasks: number;
  plannedOpenAt: string | null;
  leaseSignedAt: string | null;
  curatorName: string | null;
  pauseReason: string | null;
}

export type OpeningPlanListStatus = 'active' | 'opened' | 'all';

export interface OpeningPlansListDto {
  items: OpeningPlanListItemDto[];
}

/**
 * Сообщение в переписку: текст и/или до 10 файлов; durations — секунды по файлам.
 * null — не видео или браузер не прочитал длительность (сервер принимает и так).
 */
export interface SendThreadMessageInput {
  body: string;
  files: File[];
  durations: Array<number | null>;
}

// ───────────────────────── Спецификация оборудования (FB-34, 02.11.2026) ─────────────────────────

export type EquipmentStatus = 'not_ordered' | 'ordered' | 'received';
export type EquipmentReplacementStatus = 'pending' | 'approved' | 'rejected';

/** Позиция спецификации; количество и цена — строки, даты — `YYYY-MM-DD`. Зеркало opening-equipment.logic.ts бэкенда. */
export interface EquipmentItemDto {
  id: string;
  /** Задача карточки («этап оборудования»), в которой показываем позицию; null — этап не задан (тогда — в задаче `MyEquipmentDto.taskId`). */
  taskId?: string | null;
  name: string;
  description: string;
  qty: string;
  unit: string;
  status: EquipmentStatus;
  replacementText: string | null;
  replacementStatus: EquipmentReplacementStatus | null;
  /** Комментарий УК к решению по замене (при отказе — причина). */
  replacementComment: string | null;
  price: string | null;
  url: string | null;
  orderedAt: string | null;
  etaDate: string | null;
  sortOrder: number;
  /** template — из шаблона открытия, excel — из файла проекта, manual — внесена УК. */
  source?: 'manual' | 'excel' | 'template';
  partnerUpdatedAt: string | null;
}

export interface MyEquipmentDto {
  /** Карточка, которой принадлежит спецификация; null — карточки нет. */
  cardId: string | null;
  /** Задача «Заказ оборудования» (t074 / rb08) — показываем в ней позиции без своего этапа; null — такой задачи нет. */
  taskId: string | null;
  items: EquipmentItemDto[];
  summary: { total: number; ordered: number; received: number; replacementsPending: number };
}

/** Что партнёр может менять в позиции (остальное сервер не принимает). `null` очищает поле. */
export interface EquipmentPatch {
  status?: EquipmentStatus;
  orderedAt?: string | null;
  etaDate?: string | null;
  price?: string | null;
  url?: string | null;
  replacementText?: string | null;
}

/** Ключи react-query раздела (инвалидация из разных компонентов). */
export const OPENING_TASKS_KEYS = {
  myTasks: ['wiki', 'opening', 'my-tasks'] as const,
  /** Визард «Открытие точки»: карточка открытия целиком. */
  plan: ['wiki', 'opening', 'plan'] as const,
  /** Менеджер: список открытий по фильтру и план выбранной карточки. */
  plans: (status: OpeningPlanListStatus) => ['wiki', 'opening', 'plans', status] as const,
  planById: (id: string) => ['wiki', 'opening', 'plans', 'card', id] as const,
  thread: (id: string) => ['wiki', 'opening', 'thread', id] as const,
  /** Спецификация оборудования карточки партнёра. */
  equipment: ['wiki', 'opening', 'equipment'] as const,
};

async function readError(r: Response): Promise<ApiError> {
  let parsed: unknown = null;
  try {
    parsed = await r.json();
  } catch {
    /* не JSON */
  }
  const raw = parsed && typeof parsed === 'object' && 'message' in parsed ? (parsed as { message: unknown }).message : null;
  const msg =
    (Array.isArray(raw) ? raw.map(String).join(', ') : raw != null ? String(raw) : null) ||
    (r.status === 413 ? 'Файлы слишком большие для отправки — уменьшите или пришлите ссылкой' : `Ошибка отправки (${r.status})`);
  return new ApiError(r.status, parsed, msg);
}

/** multipart — сырой fetch с cookie (как uploadForm в learn.ts), ошибки — ApiError. */
async function postForm<T>(path: string, fd: FormData): Promise<T> {
  const r = await fetch(`/api${path}`, { method: 'POST', body: fd, credentials: 'include' });
  if (!r.ok) throw await readError(r);
  return (await r.json()) as T;
}

const enc = encodeURIComponent;

export const openingTasksApi = {
  /** Всё для блока «Открытие» на странице «Мои задачи». */
  myTasks: () => api.get<MyOpeningTasksDto>('/wiki/opening/my-tasks'),
  /** Визард «Открытие точки»: карточка открытия партнёра целиком (обзор, Гант, задачи, чек-листы, обращения). */
  plan: () => api.get<MyOpeningPlanDto>('/wiki/opening/plan'),
  /** Менеджер УК: открытия партнёров в его скоупе (403 — нет права «Этапы открытия — просмотр» или режим «от лица»). */
  plans: (status: OpeningPlanListStatus) => api.get<OpeningPlansListDto>(`/wiki/opening/plans?status=${enc(status)}`),
  /** Менеджер УК: план одной карточки — вид партнёра, только чтение (вне скоупа — 404). */
  planById: (id: string) => api.get<MyOpeningPlanDto>(`/wiki/opening/plans/${enc(id)}`),
  /** Отметить ручной пункт этапа (только `canToggle`). */
  toggleTask: (taskId: string, done: boolean) =>
    api.post<{ ok: true }>(`/wiki/opening/my-tasks/tasks/${enc(taskId)}`, { done }),
  /** Спецификация оборудования карточки партнёра (чтение разрешено и в режиме «смотреть от лица»). */
  equipment: () => api.get<MyEquipmentDto>('/wiki/opening/my-tasks/equipment'),
  /** Партнёр отмечает позицию: «заказано / получено», даты, цена, ссылка, замена. Режим «смотреть от лица» — 403. */
  updateEquipment: (itemId: string, patch: EquipmentPatch) =>
    api.post<EquipmentItemDto>(`/wiki/opening/my-tasks/equipment/${enc(itemId)}`, patch),
  /** Отметить задачу карточки («Сделано» → done, снять → todo). Режим «смотреть от лица» — 403. */
  setCardTaskStatus: (taskId: string, status: MyCardTaskSetStatus) =>
    api.post<{ ok: true }>(`/wiki/opening/my-tasks/card-tasks/${enc(taskId)}`, { status }),
  /** Даты «начали / закончим» задачи (только для информации; `null` — очистить). Режим «смотреть от лица» — 403. */
  setCardTaskPartnerDates: (taskId: string, dates: PartnerDatesPatch) =>
    api.post<{ ok: true; partnerStartDate: string | null; partnerDueDate: string | null }>(`/wiki/opening/my-tasks/card-tasks/${enc(taskId)}/partner-dates`, dates),
  /** Отметить пункт чек-листа задачи карточки; пункт-«ссылка» — вместе со ссылкой `url`. В ответе весь чек-лист. */
  setCardChecklistItem: (taskId: string, index: number, done: boolean, url?: string) =>
    api.post<{ checklist: MyCardChecklistItemDto[] }>(
      `/wiki/opening/my-tasks/card-tasks/${enc(taskId)}/checklist/${index}`,
      url ? { done, url } : { done },
    ),
  /** Подать заявку на переход на следующий этап. */
  requestNextStage: (comment?: string) =>
    api.post<StageRequestDto>('/wiki/opening/stage-request', comment ? { comment } : {}),
  /** Отозвать заявку, пока она на рассмотрении. */
  cancelStageRequest: () => api.delete<{ ok: true }>('/wiki/opening/stage-request'),
  /** Переписка по помещению (заодно помечает её прочитанной). */
  thread: (submissionId: string) =>
    api.get<RoomThreadDto>(`/wiki/opening/room-inspection/${enc(submissionId)}/thread`),
  /** Сообщение с вложениями: body, files[], durations = JSON-массив секунд по файлам. */
  sendMessage: (submissionId: string, input: SendThreadMessageInput) => {
    const fd = new FormData();
    fd.append('body', input.body);
    input.files.forEach((f) => fd.append('files', f, f.name));
    fd.append('durations', JSON.stringify(input.durations));
    return postForm<ThreadMessageDto>(`/wiki/opening/room-inspection/${enc(submissionId)}/messages`, fd);
  },
  /** Момент исправлен — отметка партнёра, дальше подтверждает куратор. */
  momentDone: (momentId: string, note?: string) =>
    api.post<MomentDto>(`/wiki/opening/moments/${enc(momentId)}/done`, note ? { note } : {}),
  /** Снять свою отметку, пока куратор не подтвердил. */
  momentUndo: (momentId: string) => api.post<MomentDto>(`/wiki/opening/moments/${enc(momentId)}/undo`),
};
